import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SEO from './SEO';
import './FAQPage.css';
import logo from '../assets/pokeshani-logo.png';

const FAQPage: React.FC = () => {
  const location = useLocation();
  
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  
  // FAQPage structured data. Every question and answer below is also present
  // verbatim in the visible page content, which is what schema guidelines
  // require. NOTE: Google retired FAQ rich results for all but a handful of
  // authoritative sites in 2023, so treat this as an entity/clarity signal
  // rather than an expected snippet win.
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    '@id': 'https://pokeshani.com/faq#faqpage',
    url: 'https://pokeshani.com/faq',
    mainEntity: [
      {
        q: 'How do payments work?',
        a: 'PokeShani uses PayPal for secure payments. When you create a product listing you add your PayPal email address. When a buyer makes a purchase, the payment goes directly to your PayPal account within seconds - no waiting and no platform fees.'
      },
      {
        q: 'How do I create a Sales Page for my Digital Product?',
        a: 'Log into your creator dashboard, click Create New Product, add your product details (title, description, price), upload a thumbnail (1200x630px), add the access URL where buyers get the product, and set your PayPal email for receiving payments. Then click Create Sales Page to publish it.'
      },
      {
        q: 'What can I sell on PokeShani?',
        a: 'Any digital product: digital downloads such as PDFs, audio and video files, software, digital art and designs, eBooks and guides, templates and themes, digital courses and tutorials, presets and filters, digital planners and worksheets, stock photos and videos, and music or sound effects. Your content must be your own original work or you must have the rights to sell it, and it must be delivered digitally.'
      },
      {
        q: 'How do I track my sales?',
        a: 'Your creator dashboard provides analytics including total revenue and sales, product performance, customer insights, transaction history, conversion rates, traffic sources and sales trends over time.'
      },
      {
        q: 'How do I purchase a digital product?',
        a: 'Find the product you want to buy, click the purchase button, complete payment through PayPal, and receive instant access to your purchased product.'
      },
      {
        q: 'Is my payment secure?',
        a: 'Yes. All payments are processed through PayPal\u2019s secure payment system. Your payment information is never stored on the PokeShani platform.'
      },
      {
        q: 'How do I access my purchased products?',
        a: 'After successful payment you receive an email with access instructions and links to download or access your purchased product.'
      },
      {
        q: 'Are there really zero platform fees?',
        a: 'Yes. PokeShani charges no platform fees, so you keep 100% of your earnings. Only the standard PayPal transaction fees apply.'
      }
    ].map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a }
    }))
  };

  return (
    <>
      <SEO 
        title="FAQ - PokeShani Help Center | Digital Products Platform"
        description="Get answers to common questions about PokeShani. Learn about zero fees, instant payouts, product creation, sales tracking, and platform features. Complete help guide for creators."
        keywords="FAQ, help center, platform guide, zero fees explained, instant payouts, product creation help, sales tracking, creator support"
        url="https://pokeshani.com/faq"
      />

      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>
      
      <div className="faq-landing-container">
        <div className="faq-header-section">
          <div className="faq-header-content">
            <Link to="/" className="faq-logo-link">
              <img src={logo} alt="PokeShani Logo" className="faq-logo" />
            </Link>
            <h1 className="faq-main-title">Frequently Asked Questions</h1>
            <p className="faq-subtitle">Learn how PokeShani works and start selling your digital products today!</p>
          </div>
        </div>
        
        <div className="faq-main-content">
          <div className="important-notice">
            <h2>⚠️ Important PayPal Account Requirements</h2>
            <p>To receive payments on PokeShani, you MUST have a verified PayPal account with no limitations. This means:</p>
            <ul>
              <li>Your PayPal account must be fully verified</li>
              <li>No active restrictions or limitations on your account</li>
              <li>Ability to receive payments in your country</li>
              <li>Complete business/personal information verification</li>
            </ul>
            <p><strong>Note:</strong> Payments will fail if your PayPal account has any limitations or is not properly verified.</p>
          </div>

          <section className="faq-section">
            <h2>For Creators</h2>
            
            <div className="faq-item">
              <h3>How do payments work?</h3>
              <p>PokeShani uses PayPal for secure payments. When you create a product listing, you'll add your PayPal email address. When a buyer makes a purchase, the payment goes directly to your PayPal account within seconds - no waiting, no platform fees!</p>
              <h4>PayPal Account Setup:</h4>
              <ol>
                <li>Create a PayPal account if you don't have one</li>
                <li>Complete account verification (upload ID, link bank account)</li>
                <li>Ensure your account can receive payments in your country</li>
                <li>Add your verified PayPal email to your PokeShani profile</li>
              </ol>
            </div>

            <div className="faq-item">
              <h3>How do I create a Sales Page for my Digital Product?</h3>
              <p>Creating a Sales page for your digital product is simple:</p>
              <ol>
                <li>Log into your creator dashboard</li>
                <li>Click "Create New Product"</li>
                <li>Add your product details (title, description, price)</li>
                <li>Upload your product thumbnail (recommended size: 1200x630px)</li>
                <li>Add the access URL where buyers can download/access your product</li>
                <li>Set your PayPal email for receiving payments</li>
                <li>Review all details carefully</li>
                <li>Click "Create Sales Page" to make your product live and generate a dedicated sales page</li>
              </ol>
              
              <h4>Important Requirements Before Creating Sales Page:</h4>
              <ul>
                <li><strong>Product Title:</strong>
                  <ul>
                    <li>Must be unique and descriptive</li>
                    <li>Minimum 5 characters, maximum 100 characters</li>
                    <li>Should clearly indicate what buyers will receive</li>
                  </ul>
                </li>
                <li><strong>Product Description:</strong>
                  <ul>
                    <li>Minimum 200 words describing your product in detail</li>
                    <li>Include key features, benefits, and what's included</li>
                    <li>Add clear usage instructions and delivery information</li>
                    <li>Specify any technical requirements or prerequisites</li>
                  </ul>
                </li>
                <li><strong>Product Price:</strong>
                  <ul>
                    <li>Must be set in USD</li>
                    <li>Minimum price: $1.00</li>
                    <li>Maximum price: $1000.00</li>
                    <li>Use whole numbers or up to 2 decimal places</li>
                  </ul>
                </li>
                <li><strong>Product Thumbnail:</strong>
                  <ul>
                    <li>Required size: 1200x630px (16:9 ratio)</li>
                    <li>Maximum file size: 5MB</li>
                    <li>Supported formats: JPG, PNG, or WebP</li>
                    <li>Must be high quality and representative of your product</li>
                  </ul>
                </li>
                <li><strong>Access URL:</strong>
                  <ul>
                    <li>Must start with https:// for security</li>
                    <li>Should be a direct download link or access page</li>
                    <li>Must be accessible and working</li>
                    <li>Recommended to use cloud storage services</li>
                  </ul>
                </li>
                <li><strong>PayPal Email:</strong>
                  <ul>
                    <li>Must be verified and active PayPal account</li>
                    <li>No restrictions or limitations on the account</li>
                    <li>Must be able to receive payments in your country</li>
                    <li>Business accounts recommended for high-volume sales</li>
                  </ul>
                </li>
              </ul>
              
              <h4>Common Errors and Solutions:</h4>
              <ul>
                <li><strong>"Invalid PayPal Email"</strong>
                  <ul>
                    <li>Ensure the email is associated with a verified PayPal account</li>
                    <li>Check for typos in the email address</li>
                    <li>Use the primary email associated with your PayPal account</li>
                    <li>Verify your PayPal account can receive payments</li>
                  </ul>
                </li>
                <li><strong>"Description Too Short"</strong>
                  <ul>
                    <li>Provide at least 200 words describing your product</li>
                    <li>Include key features and benefits</li>
                    <li>Add usage instructions if applicable</li>
                    <li>Describe the delivery process and format</li>
                  </ul>
                </li>
                <li><strong>"Invalid Access URL"</strong>
                  <ul>
                    <li>Ensure the URL starts with https://</li>
                    <li>Test the URL in a private browser window</li>
                    <li>Make sure the content is accessible</li>
                    <li>Verify the download/access link works</li>
                  </ul>
                </li>
                <li><strong>"Image Upload Failed"</strong>
                  <ul>
                    <li>Use images under 5MB in size</li>
                    <li>Ensure the format is JPG, PNG, or WebP</li>
                    <li>Try compressing the image if too large</li>
                    <li>Verify the image dimensions are 1200x630px</li>
                  </ul>
                </li>
                <li><strong>"Sales Page Creation Failed"</strong>
                  <ul>
                    <li>Check all required fields are filled correctly</li>
                    <li>Verify your PayPal email is valid</li>
                    <li>Ensure your product title is unique</li>
                    <li>Try refreshing the page and submitting again</li>
                  </ul>
                </li>
              </ul>
            </div>

            <div className="faq-item">
              <h3>What can I sell on PokeShani?</h3>
              <p>You can sell any digital products including:</p>
              <ul>
                <li>Digital downloads (PDFs, audio files, videos)</li>
                <li>Software and applications</li>
                <li>Digital art and designs</li>
                <li>eBooks and guides</li>
                <li>Templates and themes</li>
                <li>Digital courses and tutorials</li>
                <li>Presets and filters</li>
                <li>Digital planners and worksheets</li>
                <li>Stock photos and videos</li>
                <li>Music and sound effects</li>
              </ul>
              
              <h4>Content Guidelines:</h4>
              <p>Your content must:</p>
              <ul>
                <li>Be your original work or you must have rights to sell it</li>
                <li>Not violate any copyright or intellectual property laws</li>
                <li>Be delivered digitally (no physical products)</li>
                <li>Follow our community guidelines</li>
              </ul>
            </div>

            <div className="faq-item">
              <h3>How do I track my sales?</h3>
              <p>Your creator dashboard provides real-time analytics including:</p>
              <ul>
                <li>Total revenue and sales</li>
                <li>Product performance metrics</li>
                <li>Customer insights</li>
                <li>Transaction history</li>
                <li>Conversion rates</li>
                <li>Traffic sources</li>
                <li>Customer geography</li>
                <li>Sales trends over time</li>
              </ul>
              
              <h4>Sales Notifications:</h4>
              <p>You'll receive notifications for:</p>
              <ul>
                <li>New sales (instant notification)</li>
                <li>PayPal payment confirmations</li>
                <li>Customer messages</li>
                <li>Product reviews</li>
              </ul>
            </div>

            <div className="faq-item">
              <h3>Troubleshooting Payment Issues</h3>
              <p>If you're experiencing payment problems:</p>
              <ol>
                <li><strong>Verify PayPal Account Status:</strong>
                  <ul>
                    <li>Check for any account limitations</li>
                    <li>Ensure your account is verified</li>
                    <li>Confirm your receiving limit hasn't been reached</li>
                  </ul>
                </li>
                <li><strong>Update Payment Settings:</strong>
                  <ul>
                    <li>Verify your PayPal email in PokeShani settings</li>
                    <li>Ensure your PayPal account can receive payments</li>
                    <li>Check if your country supports PayPal receiving</li>
                  </ul>
                </li>
                <li><strong>Common Payment Errors:</strong>
                  <ul>
                    <li>"Payment Failed": Check PayPal account status and limitations</li>
                    <li>"Invalid Recipient": Verify PayPal email is correct</li>
                    <li>"Currency Not Supported": Ensure your PayPal account accepts USD</li>
                  </ul>
                </li>
              </ol>
            </div>
          </section>

          <section className="faq-section">
            <h2>For Buyers</h2>
            
            <div className="faq-item">
              <h3>How do I purchase a digital product?</h3>
              <p>Purchasing is straightforward:</p>
              <ol>
                <li>Find the product you want to buy</li>
                <li>Click the purchase button</li>
                <li>Complete payment through PayPal</li>
                <li>Receive instant access to your purchased product</li>
              </ol>
            </div>

            <div className="faq-item">
              <h3>Is my payment secure?</h3>
              <p>Yes! All payments are processed through PayPal's secure payment system. Your payment information is never stored on our platform.</p>
            </div>

            <div className="faq-item">
              <h3>How do I access my purchased products?</h3>
              <p>After successful payment, you'll receive an email with access instructions and links to download/access your purchased product.</p>
            </div>
          </section>

          <section className="faq-section">
            <h2>Platform Features</h2>
            
            <div className="faq-item">
              <h3>Zero Platform Fees</h3>
              <p>PokeShani doesn't charge any platform fees. You keep 100% of your earnings - only standard PayPal transaction fees apply.</p>
            </div>

            <div className="faq-item">
              <h3>Instant Payouts</h3>
              <p>No waiting periods! Payments go directly to your PayPal account the moment a sale is made.</p>
            </div>

            <div className="faq-item">
              <h3>Secure Transactions</h3>
              <p>All payments are processed through PayPal's secure system, ensuring safe transactions for both buyers and sellers.</p>
            </div>

            <div className="faq-item">
              <h3>Real-time Analytics</h3>
              <p>Track your sales, revenue, and customer insights through our detailed analytics dashboard.</p>
            </div>
          </section>
        </div>
        
        <div className="faq-footer">
          <div className="faq-footer-content">
            <p>Still have questions? <a href="https://x.com/PokeShani" target="_blank" rel="noopener noreferrer" className="contact-link">Contact our support team</a></p>
            <Link to="/" className="back-home-btn">← Back to Home</Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default FAQPage;
